import { useEffect } from 'react';
import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';
import { useCustomScrollToTop } from '@srcDS/utils/useCustomScrollToTop';
import TagManager from '../../../../classes/TagManager';
import { useCategoryContext } from '@srcDS/components/templates/category/context/useCategoryContext';
import { usePromo } from '@srcDS/hooks/usePromo';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { usePageLoading } from '@frontastic/catwalk/js/helper/hooks/usePageLoading';

interface Stream {
  count: number;
  total: number;
  items?: any[];
  projectSpecificData?: {
    merchandised_category_url?: string;
  };
}

interface Route {
  parameters?: {
    phrase?: string;
  };
}

interface Node {
  nodeType: string;
}

interface Data {
  nostoPlacementId?: string;
}

export const useCategory = () => {
  const { nostoCategoryView, nostoSearchView } = useNostoContext();
  const { goToTop } = useCustomScrollToTop();
  const { dispatchPageLoadingStart } = usePageLoading();

  const {
    isGlobalLoaderShow,
    stream,
    pushedItems,
    itemOffset,
    triggerFilter,
    itemsLimit,
    streamId,
    hasPromoTile,
    categoryId,
    categoryList,
    setPushedItems,
    route,
    data,
    node,
    isSSR,
    getListName,
    stableItems: items,
    ...rest
  } = useCategoryContext() as any;

  const searchPhrase = route?.parameters?.phrase;

  useEffect(() => {
    if (isSSR) return;
    const slicedItems = categoryList?.slice(pushedItems, (stream as Stream).count);
    setPushedItems((stream as Stream).count);
    const listName = getListName();
    if (!searchPhrase) {
      TagManager.productImpressions(slicedItems, listName);
    } else {
      TagManager.searchImpressions(slicedItems, searchPhrase, slicedItems?.length);
    }
  }, [(stream as Stream).count, isSSR]);

  useEffect(() => {
    if (isSSR) return;
    if ((node as Node).nodeType === 'search') {
      nostoSearchView(route?.parameters?.phrase, (data as Data)?.nostoPlacementId);
    } else if (stream?.projectSpecificData?.merchandised_category_url) {
      nostoCategoryView(stream?.projectSpecificData?.merchandised_category_url);
    }
  }, [isSSR, categoryId, route, stream?.projectSpecificData?.merchandised_category_url]);

  const promos = usePromo({
    data,
    hasPromoTile,
    stream,
    itemOffset,
    isGlobalLoaderShow,
    hidePromos: triggerFilter,
    isSSR
  });

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handlePageClick = (e: { selected: number }) => {
    const newOffset = (e.selected * itemsLimit) % (stream as Stream).total;
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set(`s[${streamId}][offset]`, `${newOffset}`);

    dispatchPageLoadingStart(false);
    router.push(`${pathname}?${updatedSearchParams.toString()}`);
    goToTop(true);
  };

  return {
    items: stream?.items || items,
    promoData: promos?.promoData || null,
    handlePageClick,
    ...rest,
  };
};
